import type {
  AssetsOverview,
  FinancialAssetItem,
  FinancialAssetItemFinancialSubType,
  ImmovableAssetItem,
  ImmovableAssetItemImmovableSubType,
  LiabilityItem,
  LiabilityItemLiabilitySubType,
  LoadValuesOptionExpenseType,
  MovableAssetItem,
  MovableAssetItemMovableSubType,
  ProposedSolutionStep,
} from "Infrastructure/ApiZfp/Schema";

type AssetSummaryType = {
  readonly immovableAssetsAmount: number;
  readonly movableAssetsAmount: number;
  readonly financialAssetsAmount: number;
  readonly liabilitiesAmount: number;
  readonly unusableAmount: number;
};

export const calculateAssetsCombinedValue = (
  assets: AssetsOverview | undefined,
  immovableAssetItems: ImmovableAssetItem[] | undefined,
  movableAssetItems: MovableAssetItem[] | undefined,
  financialAssetItems: FinancialAssetItem[] | undefined,
  liabilityItems: LiabilityItem[] | undefined,
): AssetSummaryType => {
  if (!assets) {
    return defaultState;
  }

  const immovableUnusable =
    assets.immovableAssetsEntryMode === "aggregate"
      ? !assets.aggregatedImmovableAssets.isUsableForGoals
        ? (assets.aggregatedImmovableAssets.aggregatedAssetsAmount?.value ?? 0)
        : 0
      : _calculateUnusable(immovableAssetItems ?? []);

  const movableUnusable =
    assets.movableAssetsEntryMode === "aggregate"
      ? !assets.aggregatedMovableAssets.isUsableForGoals
        ? (assets.aggregatedMovableAssets.aggregatedAssetsAmount?.value ?? 0)
        : 0
      : _calculateUnusable(movableAssetItems ?? []);

  const financialUnusable =
    assets.financialAssetsEntryMode === "aggregate"
      ? !assets.aggregatedFinancialAssets.isUsableForGoals
        ? (assets.aggregatedFinancialAssets.aggregatedAssetsAmount?.value ?? 0)
        : 0
      : _calculateUnusable(financialAssetItems ?? []);

  return {
    immovableAssetsAmount:
      assets.immovableAssetsEntryMode === "aggregate"
        ? (assets.aggregatedImmovableAssets.aggregatedAssetsAmount?.value ?? 0)
        : _calculate(immovableAssetItems ?? []),
    movableAssetsAmount:
      assets.movableAssetsEntryMode === "aggregate"
        ? (assets.aggregatedMovableAssets.aggregatedAssetsAmount?.value ?? 0)
        : _calculate(movableAssetItems ?? []),
    financialAssetsAmount:
      assets.financialAssetsEntryMode === "aggregate"
        ? (assets.aggregatedFinancialAssets.aggregatedAssetsAmount?.value ?? 0)
        : _calculate(financialAssetItems ?? []),
    liabilitiesAmount:
      assets.liabilitiesEntryMode === "aggregate"
        ? (assets.aggregatedLiabilities.aggregatedAssetsAmount?.value ?? 0)
        : _calculate(liabilityItems ?? []),
    unusableAmount: immovableUnusable + movableUnusable + financialUnusable,
  };
};

const defaultState: AssetSummaryType = {
  immovableAssetsAmount: 0,
  movableAssetsAmount: 0,
  financialAssetsAmount: 0,
  liabilitiesAmount: 0,
  unusableAmount: 0,
};

const _calculate = (assets: (ImmovableAssetItem | MovableAssetItem | FinancialAssetItem | LiabilityItem)[]): number => {
  return assets.reduce((acc, curr) => acc + curr.amount.value, 0);
};

const _calculateUnusable = (assets: (ImmovableAssetItem | MovableAssetItem | FinancialAssetItem)[]) => {
  return assets.reduce((acc, curr) => acc + (!curr.isUsableForGoals ? curr.amount.value : 0), 0);
};

// The new API exposes asset sub-types as lower snake_case values (e.g. "bank_account"), while the
// existing Czech translations are keyed by the old PascalCase enum member names (e.g. "BankAccount").
// The sets/ordering are identical between old and new schemas, so we bridge new value -> old
// resource key here instead of duplicating/renaming translation keys.
export const IMMOVABLE_SUBTYPE_RESOURCE_KEY: Record<
  ImmovableAssetItemImmovableSubType,
  "House" | "Apartment" | "Cabin" | "Land" | "Forest" | "Other"
> = {
  house: "House",
  apartment: "Apartment",
  cabin: "Cabin",
  land: "Land",
  forest: "Forest",
  other: "Other",
};

export const MOVABLE_SUBTYPE_RESOURCE_KEY: Record<MovableAssetItemMovableSubType, "Vehicle" | "Company" | "Other"> = {
  vehicle: "Vehicle",
  company: "Company",
  other: "Other",
};

export const FINANCIAL_SUBTYPE_RESOURCE_KEY: Record<
  FinancialAssetItemFinancialSubType,
  "Cash" | "BankAccount" | "SavingAccount" | "BuildingSaving" | "PensionSaving" | "Investment" | "Other"
> = {
  cash: "Cash",
  bank_account: "BankAccount",
  saving_account: "SavingAccount",
  building_saving: "BuildingSaving",
  pension_saving: "PensionSaving",
  investment: "Investment",
  other: "Other",
};

export const LIABILITY_SUBTYPE_RESOURCE_KEY: Record<
  LiabilityItemLiabilitySubType,
  "Mortgage" | "BuildingSaving" | "Consumer" | "CreditCard" | "Overdraft" | "Other"
> = {
  mortgage: "Mortgage",
  building_saving: "BuildingSaving",
  consumer: "Consumer",
  credit_card: "CreditCard",
  overdraft: "Overdraft",
  other: "Other",
};

export const CONTRACT_STEP_RESOURCE_KEY: Record<ProposedSolutionStep, "Keep" | "CreateRecommended" | "Modify" | "Cancel"> = {
  keep: "Keep",
  create_recommended: "CreateRecommended",
  modify: "Modify",
  cancel: "Cancel",
};

export const LOAD_VALUES_EXPENSE_TYPE_RESOURCE_KEY: Record<
  LoadValuesOptionExpenseType,
  "LifeInsurance" | "VehicleInsurance" | "AccidentInsurance" | "HouseInsurance" | "Other"
> = {
  life_insurance: "LifeInsurance",
  vehicle_insurance: "VehicleInsurance",
  accident_insurance: "AccidentInsurance",
  house_insurance: "HouseInsurance",
  other: "Other",
};

// `ContractSolutionLabel.label` is a plain string, not a typed enum — the backend sends a fixed key
// for each of the 5 named immovable coverages, but a free-form custom-coverage name for anything
// else. Only the known keys are translated; unrecognized ones (custom coverage names) pass through.
export const CONTRACT_COVERAGE_LABEL_RESOURCE_KEY: Record<
  string,
  | "PropertyInsuranceCoverage"
  | "HouseholdInsuranceCoverage"
  | "OutbuildingCoverage"
  | "OwnerLiabilityCoverage"
  | "LiabilityCoverage"
  | "VehicleCompulsoryInsuranceCoverage"
  | "VehicleCascoInsuranceCoverage"
  | "VehicleOtherInsuranceCoverage"
  | "VehicleAssistanceInsuranceCoverage"
  | "VehicleWindscreenInsuranceCoverage"
> = {
  property_insurance: "PropertyInsuranceCoverage",
  household_insurance: "HouseholdInsuranceCoverage",
  outbuilding: "OutbuildingCoverage",
  owner_liability: "OwnerLiabilityCoverage",
  liability: "LiabilityCoverage",
  vehicle_compulsory_insurance: "VehicleCompulsoryInsuranceCoverage",
  vehicle_casco_insurance: "VehicleCascoInsuranceCoverage",
  vehicle_other_insurance: "VehicleOtherInsuranceCoverage",
  vehicle_assistance_insurance: "VehicleAssistanceInsuranceCoverage",
  vehicle_windscreen_insurance: "VehicleWindscreenInsuranceCoverage",
};
